import {
  PAPER_THEME,
  auditTheme,
  contrastRatio,
  isInks,
  tenantTheme,
  type Inks,
} from "@festipal/shared";
import { useCallback, useEffect, useState } from "react";

import { ApiError, festivals } from "./api";

/**
 * Thème de l'organisateur.
 *
 * Le même contrôle d'accessibilité qu'au serveur tourne ici, à chaque frappe :
 * l'organisateur voit ce qui cloche pendant qu'il choisit ses couleurs, au lieu
 * de l'apprendre au moment d'enregistrer. Le serveur reste le juge — une
 * vérification côté navigateur ne garantit rien — mais il n'a plus à être le
 * messager.
 */

/**
 * Les encres, regroupées par usage.
 *
 * Douze champs alignés seraient illisibles ; les grouper dit à quoi chacun sert,
 * et rend évident qu'ils vont par paires — une encre et ce qu'on écrit dessus.
 */
const GROUPS: { title: string; hint: string; keys: (keyof Inks)[] }[] = [
  {
    title: "Fond et texte",
    hint: "Le papier de l'application, et l'encre qu'on y pose.",
    keys: ["paper", "paperRaised", "ink", "inkMuted"],
  },
  {
    title: "Encre d'accentuation",
    hint: "Les boutons et les éléments choisis. C'est elle qu'on remarque.",
    keys: ["accent", "onAccent"],
  },
  {
    title: "Seconde encre",
    hint: "Les mises en avant qui ne sont pas des actions.",
    keys: ["highlight", "onHighlight"],
  },
  {
    title: "Alerte",
    hint: "Ce qui a mal tourné. Verrouillé sur du rouge dans la plupart des cas.",
    keys: ["alert", "onAlert"],
  },
  {
    title: "Traits",
    hint: "Le filet sépare, la bordure entoure un champ — celle-ci doit rester visible.",
    keys: ["rule", "edge"],
  },
];

const LABELS: Record<keyof Inks, string> = {
  paper: "Fond",
  paperRaised: "Surface posée",
  ink: "Texte",
  inkMuted: "Texte secondaire",
  accent: "Accentuation",
  onAccent: "Texte sur l'accentuation",
  highlight: "Seconde encre",
  onHighlight: "Texte sur la seconde encre",
  alert: "Alerte",
  onAlert: "Texte sur l'alerte",
  rule: "Filet",
  edge: "Bordure de champ",
};

export function ThemeEditor({ slug }: { slug: string }) {
  const [inks, setInks] = useState<Inks>(PAPER_THEME.inks);
  const [themed, setThemed] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const result = await festivals.theme(slug);
      if (isInks(result.inks)) {
        setInks(result.inks);
        setThemed(true);
      } else {
        setInks(PAPER_THEME.inks);
        setThemed(false);
      }
    } catch {
      setMessage("Lecture impossible.");
    }
  }, [slug]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Contrôle immédiat, avec le même code que le serveur.
  const problems = auditTheme(tenantTheme(slug, inks));

  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await festivals.saveTheme(slug, inks);
      setThemed(true);
      setMessage(
        "Thème enregistré. Il partira sur les téléphones à la prochaine publication.",
      );
    } catch (cause) {
      setMessage(
        cause instanceof ApiError && cause.status === 422
          ? "Le serveur a refusé cette palette : elle n'est pas lisible."
          : "Enregistrement impossible.",
      );
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await festivals.saveTheme(slug, null);
      setInks(PAPER_THEME.inks);
      setThemed(false);
      setMessage("Thème retiré. Le festival reprend la charte Festipal.");
    } catch {
      setMessage("Retrait impossible.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2>Thème du festival</h2>
      <p className="muted">
        Vos couleurs remplacent les nôtres dans l'application. Les tailles de texte, les
        espacements et les zones tactiles restent inchangés : une identité forte, oui —
        une application illisible, non.
      </p>

      {message ? <p className="muted">{message}</p> : null}

      <div className="grid-2">
        <div>
          {GROUPS.map((group) => (
            <div key={group.title} style={{ marginBottom: 20 }}>
              <h3 style={{ margin: "0 0 4px", fontSize: "1rem" }}>{group.title}</h3>
              <p className="muted" style={{ margin: "0 0 8px" }}>
                {group.hint}
              </p>
              {group.keys.map((key) => (
                <label key={key} className="ink-row">
                  <span>{LABELS[key]}</span>
                  <span className="row">
                    <input
                      type="color"
                      value={inks[key]}
                      onChange={(event) =>
                        setInks((current) => ({
                          ...current,
                          [key]: event.target.value,
                        }))
                      }
                      aria-label={LABELS[key]}
                    />
                    <code>{inks[key]}</code>
                  </span>
                </label>
              ))}
            </div>
          ))}
        </div>

        <div>
          <p className="muted">Aperçu</p>
          {/* L'aperçu emploie les encres saisies, et non celles du back-office :
              c'est le seul moyen de juger une palette sans installer l'app. */}
          <div
            className="theme-preview"
            style={{ background: inks.paper, color: inks.ink, borderColor: inks.rule }}
          >
            <p
              style={{
                margin: 0,
                fontFamily: "var(--face-display)",
                fontSize: 28,
                textTransform: "uppercase",
              }}
            >
              Grande scène
            </p>
            <p style={{ margin: 0, color: inks.inkMuted }}>19 h 30 · dans 25 minutes</p>
            <div
              style={{
                padding: 12,
                borderRadius: 2,
                border: `2px solid ${inks.ink}`,
                background: inks.paperRaised,
              }}
            >
              <p style={{ margin: 0 }}>Toilettes les plus proches — 120 m</p>
            </div>
            <span
              style={{
                display: "inline-block",
                padding: "10px 16px",
                borderRadius: 2,
                background: inks.accent,
                color: inks.onAccent,
                fontFamily: "var(--face-heading)",
                textTransform: "uppercase",
                letterSpacing: 0.6,
              }}
            >
              M'y emmener
            </span>
            <span
              style={{
                display: "inline-block",
                padding: "2px 8px",
                borderRadius: 2,
                background: inks.highlight,
                color: inks.onHighlight,
                fontFamily: "var(--face-heading)",
                textTransform: "uppercase",
                letterSpacing: 0.6,
              }}
            >
              Complet
            </span>
            <p style={{ margin: 0, color: inks.alert }}>Sortie fermée côté nord</p>
          </div>

          <p className="muted" style={{ marginTop: 16 }}>
            Lisibilité
          </p>
          {problems.length === 0 ? (
            <p className="badge published">Toutes les combinaisons sont lisibles</p>
          ) : (
            <ul>
              {problems.map((problem) => (
                <li key={problem} style={{ color: "var(--alert)" }}>
                  {problem}
                </li>
              ))}
            </ul>
          )}
          <p className="muted">
            Rapport texte / fond : {contrastRatio(inks.ink, inks.paper).toFixed(2)} — il
            faut 4,5 au minimum, et davantage vaut mieux en plein soleil.
          </p>
        </div>
      </div>

      <div className="row">
        <button
          className="primary"
          onClick={() => void save()}
          disabled={busy || problems.length > 0}
        >
          Enregistrer
        </button>
        {themed ? (
          <button className="danger" onClick={() => void reset()} disabled={busy}>
            Revenir à la charte Festipal
          </button>
        ) : null}
      </div>
    </section>
  );
}
